import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { Button, InlineAlert } from '@/components/ui';
import { announcementApi, commsKeys, type ActiveAnnouncement } from '@/features/platform/api/comms';
import { useSession } from '@/lib/session';

const TONE = { info: 'info', warning: 'warning', critical: 'danger' } as const;

/** One announcement rendered as an alert. Text only — never HTML. Also used for operator previews. */
export function AnnouncementItem({ item, onDismiss, dismissing }: { item: Pick<ActiveAnnouncement, 'title' | 'body' | 'severity' | 'dismissible' | 'link'>; onDismiss?: () => void; dismissing?: boolean }) {
  const safeLink = item.link && /^https:\/\//i.test(item.link.url) ? item.link : null;
  return (
    <InlineAlert
      tone={TONE[item.severity] ?? 'info'}
      title={item.title}
      live={item.severity === 'critical' ? 'assertive' : 'polite'}
      onDismiss={item.dismissible && onDismiss && !dismissing ? onDismiss : undefined}
      action={
        safeLink ? (
          <a className="btn btn-secondary btn-sm" href={safeLink.url} target="_blank" rel="noopener noreferrer">
            {safeLink.label}
            <ExternalLink size={13} aria-hidden />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        ) : undefined
      }
    >
      {item.body ? <span style={{ whiteSpace: 'pre-line' }}>{item.body}</span> : null}
    </InlineAlert>
  );
}

/**
 * Platform announcements for the signed-in user (highest severity first, as sorted by the API).
 * Shows the first announcement and lets the user expand the rest. Mount once per shell, above the
 * routed content.
 */
export function AnnouncementBar() {
  const { user } = useSession();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const query = useQuery({
    queryKey: commsKeys.activeAnnouncements,
    queryFn: ({ signal }) => announcementApi.active(signal),
    enabled: !!user,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => announcementApi.dismiss(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: commsKeys.activeAnnouncements });
      const prev = qc.getQueryData<ActiveAnnouncement[]>(commsKeys.activeAnnouncements);
      if (prev) qc.setQueryData(commsKeys.activeAnnouncements, prev.filter((a) => a.id !== id));
      return { prev };
    },
    onError: (_e, _id, ctx) => ctx?.prev && qc.setQueryData(commsKeys.activeAnnouncements, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: commsKeys.activeAnnouncements }),
  });

  const items = query.data ?? [];
  if (!user || items.length === 0) return null;
  const shown = expanded ? items : items.slice(0, 1);

  return (
    <section aria-label="Platform announcements" className="stack" style={{ ['--stack-gap' as string]: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
      {shown.map((a) => (
        <AnnouncementItem key={a.id} item={a} onDismiss={() => dismiss.mutate(a.id)} dismissing={dismiss.isPending && dismiss.variables === a.id} />
      ))}
      {items.length > 1 && (
        <div>
          <Button variant="link" size="sm" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
            {expanded ? 'Show fewer announcements' : `Show ${items.length - 1} more announcement${items.length - 1 === 1 ? '' : 's'}`}
          </Button>
        </div>
      )}
    </section>
  );
}
